<script setup>
import { ref, defineProps, defineEmits, watch } from "vue";
import ArgonButton from "@/components/ArgonButton.vue";
import Swal from "sweetalert2";
import axios from "axios";
import Distelect from "@/utilities/Distelec.json"; // Import JSON
import { BACK_END_URL } from '@/utilities/config';
const userRole = sessionStorage.getItem("usuarioRol"); // Obtener el rol del usuario desde sessionStorage

const props = defineProps({
  id: Number,  
  name: String,  
  email: String,
  telefono: String,
  rol: String,
  junta: Number,
  provincia: String,
  canton: String,
  distrito: String,
});

const emit = defineEmits(["close"]);

// Variables reactivas para manejar los datos del formulario
const usuarioNombre = ref(props.name);
const email = ref(props.email);
const telefono = ref(props.telefono);
const rol = ref(props.rol);
const junta = ref(props.junta);
const codelec = ref(props.codelec);
const message = ref("");
const errors = ref([]);

// funcion para generar el código de la junta a partir de la provincia, cantón y distrito seleccionados
const generateCodelec = () => {
  const provinciaCode = selectedProvincia.value; 
  const cantonCode = selectedCanton.value; 
  const distritoCode = selectedDistrito.value; 

  if (provinciaCode && cantonCode && distritoCode) {
    return `${provinciaCode}${cantonCode}${distritoCode}`; // IDs concatenados
  }
  return null; // Devuelve null si falta algún dato
};


// Validación del formulario
const validateForm = () => {
  errors.value = [];
  message.value = "";

  if (!usuarioNombre.value.trim()) {
    errors.value.push("El nombre es obligatorio.");
  }

  const emailPattern = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
  if (!email.value.trim()) {
    errors.value.push("El correo electrónico es obligatorio.");
  } else if (!emailPattern.test(email.value)) {
    errors.value.push("El correo electrónico no es válido.");
  }

  const phonePattern = /^[0-9]{8}$/; 
  if (!telefono.value.trim()) {
    errors.value.push("El teléfono es obligatorio.");
  } else if (!phonePattern.test(telefono.value)) {
    errors.value.push("El teléfono no es válido.");
  }

  if (!junta.value) {
    errors.value.push("La junta es obligatoria.");
  }
 

  return errors.value.length === 0; 
};


watch(() => props.name, (newNombreValue) => {
  usuarioNombre.value = newNombreValue;
});

watch(() => props.email, (newEmailValue) => {
  email.value = newEmailValue;
});

watch(() => props.telefono, (newTelefonoValue) => {
  telefono.value = newTelefonoValue;
});

watch(() => props.rol, (newRolValue) => {
  rol.value = newRolValue;
});

watch(() => props.junta, (newJuntaValue) => {
  junta.value = newJuntaValue;
});

watch(() => props.codelec, (newCodelecValue) => {
  codelec.value = newCodelecValue;
});

const provinciaOptions = Object.entries(Distelect.provincias).map(([key, value]) => ({
  id: key,  
  name: value.nombre,
}));

const cantonOptions = ref([]);
const distritoOptions = ref([]);

const selectedProvincia = ref(props.provincia.id);
const selectedCanton = ref(props.canton.id);
const selectedDistrito = ref(props.distrito.id);

watch(selectedProvincia, (newProvincia) => {
  cantonOptions.value = Object.entries(Distelect.provincias[newProvincia]?.cantones || {}).map(([key, value]) => ({
    id: key,
    name: value.nombre,
  }));
  selectedCanton.value = ''; 
  selectedDistrito.value = ''; // Reinicia el cantón y distrito seleccionados
});

watch(selectedCanton, (newCanton) => {
  distritoOptions.value = Object.entries(Distelect.provincias[selectedProvincia.value]?.cantones[newCanton]?.distritos || {}).map(([key, value]) => ({
    id: key,
    name: value,
  }));
  selectedDistrito.value = ''; // Reinicia el distrito seleccionado
});

// Función para actualizar los datos del usuario
const handleUpdateUser = async () => {
  errors.value = [];
  message.value = "";
  if (!validateForm()) { 
    return;
  }

  // Prepara datos para enviar
  const updateData = {};

  if (usuarioNombre.value !== props.name) updateData.nombre_completo = `${usuarioNombre.value}`;
  if (email.value !== props.email) updateData.email = email.value;
  if (telefono.value !== props.telefono) updateData.telefono = telefono.value;
  if (rol.value !== props.rol) updateData.rol = rol.value;
  if (junta.value !== props.junta) updateData.junta = junta.value;

  const generatedCodelec = generateCodelec();
  if (generatedCodelec && generatedCodelec !== props.codelec) {
    updateData.codelec = generatedCodelec;
  }

  if (Object.keys(updateData).length === 0) {
    Swal.fire({
      icon: "info",
      title: "No se realizaron cambios",
      text: "No hay cambios en los datos para actualizar.",
      customClass: {
        confirmButton: "btn bg-gradient-info",
      },
      buttonsStyling: false,
    });
    return;
  }

  try {
    Swal.fire({
      title: "Actualizando datos del usuario...",
      text: "Por favor, espera mientras se actualizan los datos.",
      allowOutsideClick: false,
      showConfirmButton: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    const token = sessionStorage.getItem("token");

    const response = await axios.put(
      `${BACK_END_URL}/auth/update/usuarios/${props.id}`,
      updateData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (response.status === 200) {
      Swal.fire({
        icon: "info",
        title: "Usuario actualizado correctamente.",
        customClass: {
          confirmButton: "btn bg-gradient-info",
        },
        buttonsStyling: false,
      });

      // Close modal and refresh
      closeModal();
      location.reload();
    }
  } catch (error) {
    Swal.fire({
      icon: "error",
      title: "Error",
      text: "Ocurrió un error al actualizar el usuario",
      customClass: {
        confirmButton: "btn bg-gradient-danger",
      },
      buttonsStyling: false,
    });
  }
};

const closeModal = () => {
  emit("close");
  message.value = "";
  errors.value = [];
  usuarioNombre.value = props.name;
  email.value = props.email;
  telefono.value = props.telefono;
  rol.value = props.rol;
  junta.value = props.junta;
  codelec.value = props.codelec;
};
</script>


<template>
  <div class="modal-overlay" @click.self="closeModal">
    <div class="modal-content card">
      <div class="modal-header">
        <h5 class="modal-title">Actualizar Datos del Usuario</h5>
        <button type="button" class="btn-close" @click="closeModal" aria-label="Close"></button>
      </div>

      <!-- Contenido del formulario -->
      <form @submit.prevent="handleUpdateUser">
        <div class="modal-body">
          <div class="row">
            <div v-if="(props.rol === 'usuario' && userRole != 'usuario') || userRole != 'usuario'" class="col-md-6">
              <label>
                <i class="fas fa-user pe-2"></i> Nombre
              </label>
              <input class="form-control mb-3" v-model="usuarioNombre" type="text" />
            </div>

            <div class="col-md-6">
              <div v-if="userRole != 'usuario'" class="col-md-6">
                <label>
                  <i class="fas fa-user-cog pe-2"></i> Rol
                </label>
                <select class="form-control mb-3" v-model="rol">
                  <option value="administrador">Administrador</option>
                  <option value="usuario">Colaborador</option>
                </select>
              </div>
            </div>
          </div>

          <div class="row">
            <div class="col-md-6">
              <label>
                <i class="fas fa-envelope pe-2"></i> Correo Electrónico
              </label>
              <input class="form-control mb-3" v-model="email" type="email" :placeholder="props.email" />
            </div>
            <div class="col-md-6">
              <label>
                <i class="fas fa-phone-square-alt pe-2"></i> Teléfono
              </label>
              <input class="form-control mb-3" v-model="telefono" type="text" :placeholder="props.phone" />
            </div>
          </div>    

          <div  v-if="(props.rol === 'usuario' && userRole != 'usuario') || userRole != 'usuario'" class="row">
            <div class="col-md-4">
              <label>
                <i class="fas fa-city pe-2"></i> Provincia
              </label>
              <select v-model="selectedProvincia" class="form-control mb-3">
                <option value="" disabled selected>Select Provincia</option>
                <option v-for="provincia in provinciaOptions" :key="provincia.id" :value="provincia.id">{{ provincia.name }}</option>
              </select>
            </div>

            <div class="col-md-4">
              <label>
                <i class="fas fa-building pe-2"></i> Canton
              </label>
              <select v-model="selectedCanton" class="form-control mb-3" :disabled="!selectedProvincia">
                <option value="" disabled selected>Select Canton</option>
                <option v-for="canton in cantonOptions" :key="canton.id" :value="canton.id">{{ canton.name }}</option>
              </select>
            </div>

            <div class="col-md-4">
              <label>
                <i class="fas fa-home pe-2"></i> Distrito
              </label>
              <select v-model="selectedDistrito" class="form-control mb-3" :disabled="!selectedCanton">
                <option value="" disabled selected>Select Distrito</option>
                <option v-for="distrito in distritoOptions" :key="distrito.id" :value="distrito.id">{{ distrito.name }}</option>
              </select>
            </div>

            <div class="row">
              <div class="col-md-6">
                <label>
                  <i class="fas fa-school text-info"></i> <i class="fas fa-flag text-danger pe-2"></i>  Junta
                </label>
                <input class="form-control mb-3" v-model="junta" type="number" :placeholder="props.junta" />
              </div>
            </div>
          </div>

          <div v-if="message" class="alert alert-success">{{ message }}</div>
          <div v-if="errors.length" class="alert alert-danger">
            <ul>
              <li v-for="(error, index) in errors" :key="index">{{ error }}</li>
            </ul>
          </div>

        </div>

        <div class="modal-footer">
          <argon-button type="submit" color="dark" variant="gradient">
            Actualizar
          </argon-button>
        </div>
      </form>
    </div>
  </div>
</template>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 24, 6, 0.384);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
}

.modal-content {
  background: #fff;
  border-radius: 8px;
  max-width: 800px;
  width: 90%;
  padding: 20px;
  position: relative;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #dee2e6;
  padding-bottom: 10px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.btn-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #000;
  /* Asegura que la "X" tenga un color visible */
  cursor: pointer;
  display: flex;
  align-items: center;
}


.btn-close::before {
  content: "×";
  font-weight: bold;
}

.btn-close:hover::before {
  color: #ff0000;
  /* Cambia de color al pasar el cursor, si lo deseas */
}

@media (max-width: 768px) {
  .modal-content {
    width: 95%;
    max-width: 400px;
    padding: 15px;
  }

  .modal-header {
    flex-direction: column;
    align-items: flex-start;
    text-align: left;
  }

  .modal-title {
    font-size: 1.2rem;
  }

  .btn-close {
    font-size: 1.2rem;
    align-self: flex-end;
  }

  .modal-footer {
    flex-direction: column;
    align-items: stretch;
  }

  .argon-button {
    width: 100%;
  }

  .toggle-password {
    font-size: 1rem;
    right: 10px;
  }

  input {
    font-size: 1rem;
    padding: 10px;
  }
}
</style>
